import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ProbeConnection, TargetInfo } from './types';
import { WebUSBProbe } from './WebUSBProbe';
import { MCUReader } from './MCUReader';

// ============================================================================
// Probe Context — React context for sharing probe connection state
// ============================================================================

interface ProbeState {
  probe: ProbeConnection | null;
  reader: MCUReader | null;
  targetInfo: TargetInfo | null;
  isConnecting: boolean;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

const ProbeContext = createContext<ProbeState | null>(null);

export const ProbeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [probe, setProbe] = useState<ProbeConnection | null>(null);
  const [reader, setReader] = useState<MCUReader | null>(null);
  const [targetInfo, setTargetInfo] = useState<TargetInfo | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);
    try {
      const newProbe = new WebUSBProbe();
      const info = await newProbe.connect();
      setProbe(newProbe);
      setReader(new MCUReader(newProbe));
      setTargetInfo(info);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setProbe(null);
      setReader(null);
      setTargetInfo(null);
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (probe) {
      await probe.disconnect();
      setProbe(null);
      setReader(null);
      setTargetInfo(null);
    }
  }, [probe]);

  return (
    <ProbeContext.Provider value={{ probe, reader, targetInfo, isConnecting, error, connect, disconnect }}>
      {children}
    </ProbeContext.Provider>
  );
};

export function useProbeContext(): ProbeState {
  const ctx = useContext(ProbeContext);
  if (!ctx) {
    throw new Error('useProbeContext must be used within a ProbeProvider');
  }
  return ctx;
}
